import { useCallback, useEffect, useState } from 'react'

export const ACTIVE_KEY = 'bp:active-markedsomrade-id'

// The browser's `storage` event only fires in OTHER tabs, never the writing
// tab — so multiple instances of this hook in the same tab cannot rely on it
// for synchronisation. We dispatch this custom event on every set/clear so
// in-tab consumers stay in sync as well.
const ACTIVE_CHANGED_EVENT = 'bp:active-markedsomrade-changed'

function readStored(): number | null {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(ACTIVE_KEY)
  if (!raw) return null
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) ? n : null
}

/**
 * Persistent client-side selection of "the active markedsområde".
 * Lives in localStorage so the choice survives page reloads and is
 * shared across tabs.
 *
 * Two sync mechanisms run in parallel:
 *   - `storage` event for cross-tab sync (browser-native).
 *   - `bp:active-markedsomrade-changed` custom event for same-tab sync,
 *     since the `storage` event does not fire in the writing tab.
 *
 * Used by `useMinePostnummer` to resolve the postnumre comma-string
 * for analytics endpoints, and by `PostnummerFilter` to render the
 * active-område dropdown.
 */
export function useActiveMarkedsomrade() {
  const [activeId, setActiveIdState] = useState<number | null>(() => readStored())

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVE_KEY) setActiveIdState(readStored())
    }
    const onLocalChange = () => setActiveIdState(readStored())
    window.addEventListener('storage', onStorage)
    window.addEventListener(ACTIVE_CHANGED_EVENT, onLocalChange)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(ACTIVE_CHANGED_EVENT, onLocalChange)
    }
  }, [])

  const setActiveId = useCallback((id: number) => {
    window.localStorage.setItem(ACTIVE_KEY, String(id))
    setActiveIdState(id)
    window.dispatchEvent(new CustomEvent(ACTIVE_CHANGED_EVENT))
  }, [])

  const clearActive = useCallback(() => {
    window.localStorage.removeItem(ACTIVE_KEY)
    setActiveIdState(null)
    window.dispatchEvent(new CustomEvent(ACTIVE_CHANGED_EVENT))
  }, [])

  return { activeId, setActiveId, clearActive }
}
